import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { homeServices } from '@/mocks/en/home';

export default function Home() {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const isVisible = (id: string) => visibleSections.has(id);

  return (
    <div className="bg-cream min-h-screen">
      <Navigation />

      <main>
        {/* Hero */}
        <section className="relative min-h-screen flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20high-end%20professional%20editorial%20background%20photograph%20for%20a%20luxury%20pet%20care%20website%20The%20scene%20is%20a%20sun-drenched%20sophisticated%20modern%20apartment%20or%20boutique%20hotel%20lounge%20The%20visual%20aesthetic%20strictly%20adheres%20to%20the%20Modern%20Earth%20color%20palette%20Creamy%20Off-white%20walls%20deep%20Forest%20Green%20velvet%20textures%20on%20furniture%20and%20warm%20Burnt%20Sienna%20wood%20accents%20In%20the%20scene%20a%20group%20of%20happy%20well-groomed%20dogs%20are%20relaxing%20and%20interacting%20with%20genuine%20joy%20The%20group%20must%20include%20a%20sophisticated%20Standard%20Poodle%20with%20a%20stylish%20clip%20looking%20happy%20a%20smiling%20alert%20German%20Shepherd%20lounging%20comfortably%20and%20a%20majestic%20super-fluffy%20Chow%20Chow%20looking%20relaxed%20and%20calm%20The%20mood%20is%20pure%20warmth%20trust%20and%20happiness%20The%20lighting%20is%20soft%20and%20cinematic%20highlighting%20the%20different%20textures%20of%20the%20dogs%20fur%20against%20the%20luxurious%20interior%20Shallow%20depth%20of%20field%20focused%20on%20the%20dogs%20with%20the%20background%20elements%20beautifully%20blurred%20to%20ensure%20white%20overlay%20text%20remains%20readable&width=1920&height=1080&seq=41&orientation=landscape"
              alt="Happy dogs in a luxurious modern interior"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-forest/40" />
          </div>

          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-32 pb-20">
            <div className="max-w-3xl">
              <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-cream leading-[0.95] mb-8">
                Care for the<br />
                ones who<br />
                <span className="italic">love you</span>
              </h1>
              <p className="font-grotesk text-lg md:text-xl text-cream/80 max-w-xl leading-relaxed mb-10">
                Dog walking, sitting, training, grooming, and bespoke matchmaking.
                Thoughtful care for urban pet parents.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/services"
                  className="font-grotesk text-center whitespace-nowrap transition-opacity duration-200 hover:opacity-80"
                  style={{
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    borderRadius: '0px',
                    padding: '16px 36px',
                    display: 'inline-block',
                  }}
                >
                  Explore Services
                </Link>
                <Link
                  to="/contact"
                  className="font-grotesk text-center whitespace-nowrap transition-opacity duration-200 hover:opacity-80"
                  style={{
                    backgroundColor: '#B15B28',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    borderRadius: '0px',
                    padding: '16px 36px',
                    display: 'inline-block',
                  }}
                >
                  Book a Session
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Preview */}
        <section id="services-preview" data-animate className={`px-6 md:px-12 lg:px-20 py-24 md:py-32 transition-all duration-700 ${isVisible('services-preview') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl text-forest">Our Services</h2>
            </div>
            <Link to="/services" className="font-grotesk text-sm text-forest hover:text-sienna transition-colors mt-4 md:mt-0 flex items-center gap-2">
              View All Services <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {homeServices.map((service, i) => (
              <div
                key={service.id}
                className="group relative overflow-hidden border border-forest/10 transition-all duration-500 hover:border-forest/30"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="font-serif text-2xl text-forest">{service.title}</h3>
                    <span className="font-grotesk text-sm text-sienna whitespace-nowrap ml-4">{service.price}</span>
                  </div>
                  <p className="font-grotesk text-sm text-forest/70 leading-relaxed mb-6">{service.description}</p>
                  <Link
                    to="/services"
                    className="font-grotesk text-sm text-forest hover:text-sienna transition-colors inline-flex items-center gap-2"
                  >
                    Learn More <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Matchmaking Teaser */}
        <section id="match-preview" data-animate className={`px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-forest transition-all duration-700 ${isVisible('match-preview') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <p className="font-grotesk text-micro uppercase tracking-widest text-sienna mb-6 font-extrabold">Flagship Service</p>
              <h2 className="font-serif text-4xl md:text-5xl text-cream leading-[0.95] mb-8">
                Find your<br />
                <span className="italic">perfect match</span>
              </h2>
              <p className="font-grotesk text-base text-cream/60 leading-relaxed mb-10">
                Matchmaking is an art of compatibility. Our consultants hand-pick the perfect companion for your lifestyle — a service unlike any other.
              </p>
              <Link
                to="/services#matchmaking"
                className="inline-block font-grotesk text-xs font-bold uppercase tracking-widest text-cream border border-cream/40 px-8 py-4 hover:bg-cream/10 transition-colors whitespace-nowrap"
              >
                Begin the Journey
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="https://static.readdy.ai/image/1f65bef57b9643672cbad222c3f4355a/89c2d52ccbb990c330171fb2d93b3b30.webp"
                alt="Two dogs in a luxury garden setting"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </section>


      </main>

      <Footer />
    </div>
  );
}